import React, { useCallback, useEffect, useMemo, useState } from "react";
import { displayName } from "@/lib/names";
import axios from "axios";
import { dateLabel, lifeLabel } from "@/lib/dates";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, BookUser } from "lucide-react";
import { PersonAvatar } from "@/components/PersonAvatar";
import { VerificationBadge } from "@/components/VerificationBadge";
import { ProvisionalTag } from "@/components/PersonPicker";
import { ContributionDialog } from "@/components/ContributionDialog";
import { ContributionsList } from "@/components/ContributionsList";

export const missingInfo = (p) => {
  const m = [];
  if (p.is_provisional) m.push("Fiche provisoire");
  if (!p.birth_year) m.push("Année de naissance");
  if (!p.clan_key) m.push("Famille");
  if (!p.father_id && !p.mother_id) m.push("Parents");
  if (!p.gender || p.gender === "unknown") m.push("Genre");
  if (!(p.sources || []).length) m.push("Source");
  return m;
};

export const yearLabel = (p) => p.birth_year ? dateLabel(p, "birth") : "date inconnue";

const Row = ({ p, clan, children }) => (
  <div className="heritage-card p-3 flex items-center gap-3" data-testid={`registry-row-${p.id}`}>
    <PersonAvatar person={p} size={40} />
    <div className="flex-1 min-w-0">
      <Link to={`/persons/${p.id}`} className="font-serif-heritage text-lg text-[#261911] hover:text-[#993D22]">
        {displayName(p)}
      </Link>
      {p.is_provisional && <ProvisionalTag />}
      <div className="text-xs text-[#6E5B4F]">{yearLabel(p)}{clan ? ` · ${clan.name}` : ""}</div>
      {children}
    </div>
    <VerificationBadge status={p.verification_status || "to_verify"} withLabel={false} />
  </div>
);

const Filters = ({ q, setQ, clan, setClan, gen, setGen, clans, gens }) => (
  <div className="flex flex-col sm:flex-row gap-3 mb-6">
    <div className="relative flex-1">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E5B4F]" />
      <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un nom" className="pl-10 bg-[#FAF7F2]" data-testid="registry-search" />
    </div>
    <select value={clan} onChange={e => setClan(e.target.value)} className="h-9 rounded-md border border-input bg-[#FAF7F2] px-3 text-sm" data-testid="registry-clan-filter">
      <option value="">Toutes les familles</option>
      {clans.map(c => <option key={c.key} value={c.key}>{c.name}</option>)}
    </select>
    <select value={gen} onChange={e => setGen(e.target.value)} className="h-9 rounded-md border border-input bg-[#FAF7F2] px-3 text-sm" data-testid="registry-generation-filter">
      <option value="">Toutes les générations</option>
      {gens.map(g => <option key={g} value={g}>Génération {g}</option>)}
    </select>
  </div>
);

export const RegistryPage = () => {
  const { API, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "all";
  const [persons, setPersons] = useState([]);
  const [clans, setClans] = useState([]);
  const [contribs, setContribs] = useState([]);
  const [q, setQ] = useState("");
  const [clan, setClan] = useState("");
  const [gen, setGen] = useState("");
  const isOfficer = user?.is_super_admin || user?.role === "admin";

  const load = useCallback(async () => {
    const [ps, cl, co] = await Promise.all([
      axios.get(`${API}/persons`), axios.get(`${API}/clans`), axios.get(`${API}/contributions`),
    ]);
    setPersons(ps.data); setClans(cl.data); setContribs(co.data);
  }, [API]);
  useEffect(() => { if (user) load(); }, [user, load]);

  const clanBy = useMemo(() => Object.fromEntries(clans.map(c => [c.key, c])), [clans]);
  const gens = useMemo(() => [...new Set(persons.map(p => p.generation))].sort((a, b) => a - b), [persons]);
  const filtered = useMemo(() => persons.filter(p =>
    (!clan || p.clan_key === clan) && (!gen || String(p.generation) === gen) &&
    (!q || `${displayName(p)} ${p.nickname || ""}`.toLowerCase().includes(q.toLowerCase()))
  ), [persons, clan, gen, q]);
  const incomplete = filtered.filter(p => missingInfo(p).length > 0);
  const grouped = useMemo(() => filtered.reduce((acc, p) => { (acc[p.generation] ||= []).push(p); return acc; }, {}), [filtered]);
  const openContribs = contribs.filter(c => c.status === "pending" || c.status === "info_requested");

  if (!user) return <div className="p-10 text-center text-[#6E5B4F]">Connectez-vous pour consulter le registre.</div>;

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10" data-testid="registry-page">
      <div className="label-uppercase text-[#993D22] mb-2">Généalogie</div>
      <h1 className="font-serif-heritage text-4xl sm:text-5xl text-[#261911] mb-2 flex items-center gap-3">
        <BookUser className="w-10 h-10 text-[#D9822B]" /> Registre des personnes
      </h1>
      <p className="text-[#6E5B4F] mb-8 max-w-3xl">Classement automatique des plus anciennes générations aux plus jeunes, d'après la filiation puis l'année de naissance. Aucune date n'est inventée : les personnes sans date suivent leurs liens familiaux ou l'ordre manuel.</p>

      <Filters {...{ q, setQ, clan, setClan, gen, setGen, clans, gens }} />

      <Tabs value={tab} onValueChange={v => setParams({ tab: v })}>
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="all" data-testid="tab-all">Toutes ({filtered.length})</TabsTrigger>
          <TabsTrigger value="incomplete" data-testid="tab-incomplete">Fiches à compléter ({incomplete.length})</TabsTrigger>
          <TabsTrigger value="contributions" data-testid="tab-contributions">{isOfficer ? "Propositions à examiner" : "Mes propositions"} ({openContribs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          {Object.keys(grouped).length === 0 && <p className="text-sm italic text-[#6E5B4F]">Aucune personne.</p>}
          {Object.entries(grouped).map(([g, list]) => (
            <section key={g} className="mb-8" data-testid={`generation-${g}`}>
              <h2 className="font-serif-heritage text-2xl text-[#6B4226] mb-3 border-b border-[#E3D5CA] pb-1">Génération {g}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {list.map(p => <Row key={p.id} p={p} clan={clanBy[p.clan_key]} />)}
              </div>
            </section>
          ))}
        </TabsContent>

        <TabsContent value="incomplete">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {incomplete.map(p => (
              <Row key={p.id} p={p} clan={clanBy[p.clan_key]}>
                <div className="flex flex-wrap gap-1 mt-1">
                  {missingInfo(p).map(m => <span key={m} className="text-[10px] px-1.5 py-0.5 rounded bg-[#FBEDD6] text-[#A96B18]">{m}</span>)}
                </div>
                <div className="mt-2"><ContributionDialog person={p} allPersons={persons} clans={clans} onDone={load} /></div>
              </Row>
            ))}
            {incomplete.length === 0 && <p className="text-sm italic text-[#6E5B4F]">Toutes les fiches sont complètes.</p>}
          </div>
        </TabsContent>

        <TabsContent value="contributions">
          <ContributionsList items={contribs} allPersons={persons} clans={clans} isOfficer={isOfficer} onChanged={load} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RegistryPage;
